import { makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { ChevronDown16Regular, ChevronRight16Regular } from "@fluentui/react-icons";
import { useState, type ReactNode } from "react";
import { SectionTitle } from "@/components/ui-typography";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    padding: "12px 14px",
    boxSizing: "border-box",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground2,
    minHeight: "24px",
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    flex: 1,
  },
  titleText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },
  icon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  toggle: {
    display: "inline-flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    margin: 0,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXXS}`,
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalXXS})`,
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    cursor: "pointer",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  hintBadge: {
    padding: "1px 6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase100,
    lineHeight: "14px",
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  content: {
    minWidth: 0,
  },
});

export interface DetailSectionProps {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  variant?: "default" | "card";
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
}

export function DetailSection({
  icon,
  title,
  hint,
  action,
  children,
  collapsible = false,
  defaultOpen = true,
  open: controlledOpen,
  onToggle,
  variant = "default",
  className,
  headerClassName,
  contentClassName,
}: DetailSectionProps) {
  const styles = useStyles();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const expanded = !collapsible || isOpen;

  const handleToggle = () => {
    const next = !expanded;
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
    onToggle?.(next);
  };

  const renderHint = () => {
    if (hint === undefined || hint === null) return null;
    if (typeof hint === "string" || typeof hint === "number") {
      return <span className={styles.hintBadge}>{hint}</span>;
    }
    return <span className={styles.hint}>{hint}</span>;
  };

  const heading = (
    <SectionTitle as="span" className={styles.titleWrap}>
      {collapsible &&
        (expanded ? (
          <ChevronDown16Regular className={styles.chevron} />
        ) : (
          <ChevronRight16Regular className={styles.chevron} />
        ))}
      {icon && <span className={styles.icon}>{icon}</span>}
      <span className={styles.titleText}>{title}</span>
      {renderHint()}
    </SectionTitle>
  );

  return (
    <section className={mergeClasses(styles.root, variant === "card" && styles.card, className)}>
      <div className={mergeClasses(styles.head, headerClassName)}>
        {collapsible ? (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            onClick={handleToggle}
          >
            {heading}
          </button>
        ) : (
          heading
        )}
        {action}
      </div>
      {expanded ? <div className={mergeClasses(styles.content, contentClassName)}>{children}</div> : null}
    </section>
  );
}
